import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Save, Building2, User, Trash2, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Manage a PM company's billing profiles (Main AP + per-manager subaccounts).
 * Mirrors the QuickBooks parent → subcustomer tiering. Main AP is auto-created and
 * cannot be deleted; manager subaccounts always CC Main AP on invoices.
 */
export const PmBillingProfilesModal = ({ company, onClose }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', invoice_emails: '', cc_emails: '', billing_address: '', quickbooks_ref: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pm/companies/${company.id}/billing-profiles`, { withCredentials: true });
      setProfiles(data.profiles || []);
    } catch { toast.error('Could not load billing profiles'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company?.id]);

  const saveField = async (p, patch) => {
    try {
      const { data } = await axios.put(`${API}/admin/pm/billing-profiles/${p.id}`, patch, { withCredentials: true });
      setProfiles((list) => list.map((x) => (x.id === p.id ? data : x)));
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const createProfile = async () => {
    if (!draft.name.trim()) { toast.error('Name is required'); return; }
    try {
      await axios.post(`${API}/admin/pm/companies/${company.id}/billing-profiles`, {
        name: draft.name, kind: 'manager',
        invoice_emails: draft.invoice_emails, cc_emails: draft.cc_emails,
        billing_address: draft.billing_address, quickbooks_ref: draft.quickbooks_ref,
      }, { withCredentials: true });
      toast.success('Billing profile added');
      setAdding(false); setDraft({ name: '', invoice_emails: '', cc_emails: '', billing_address: '', quickbooks_ref: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const deactivate = async (p) => {
    if (!window.confirm(`Deactivate billing profile "${p.name}"?`)) return;
    try { await axios.delete(`${API}/admin/pm/billing-profiles/${p.id}`, { withCredentials: true }); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="pm-billing-profiles-modal" className="bg-[#141414] border border-[#262626] rounded-lg w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-[#262626] sticky top-0 bg-[#141414] z-10">
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]">BILLING PROFILES</p>
            <h3 className="text-white font-['Outfit'] text-xl font-bold flex items-center gap-2"><Building2 className="w-5 h-5 text-[#D4AF37]" /> {company.name}</h3>
            <p className="text-[#A3A3A3] text-xs mt-1">Main AP + per-manager subaccounts. Manager invoices auto-CC Main AP.</p>
          </div>
          <button onClick={onClose} data-testid="pm-billing-close" className="text-[#525252] hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          {loading ? <p className="text-[#A3A3A3] text-sm">Loading…</p> : profiles.map((p) => (
            <div key={p.id} data-testid={`pm-billing-profile-${p.id}`} className={`rounded-md border p-3 ${p.kind === 'main_ap' ? 'border-[#D4AF37]/40 bg-[#D4AF37]/5' : 'border-[#262626] bg-[#0f0f0f]'}`}>
              <div className="flex items-center gap-2 mb-2">
                {p.kind === 'main_ap' ? <Star className="w-4 h-4 text-[#D4AF37]" /> : <User className="w-4 h-4 text-cyan-300" />}
                <span className="text-white text-sm font-semibold">{p.name}</span>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${p.kind === 'main_ap' ? 'text-[#D4AF37] border-[#D4AF37]/40' : 'text-cyan-300 border-cyan-700/40'}`}>{p.kind === 'main_ap' ? 'MAIN AP' : 'MANAGER'}</span>
                {p.kind !== 'main_ap' && <button onClick={() => deactivate(p)} data-testid={`pm-billing-del-${p.id}`} className="ml-auto text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <LabeledInput label="Invoice email(s)" testid={`pm-billing-emails-${p.id}`} defaultValue={(p.invoice_emails || []).join(', ')} placeholder="ap@company.com" onBlur={(v) => saveField(p, { invoice_emails: v.split(/[,;\s]+/).filter(Boolean) })} />
                <LabeledInput label="CC email(s)" testid={`pm-billing-cc-${p.id}`} defaultValue={(p.cc_emails || []).join(', ')} placeholder={p.kind === 'main_ap' ? '' : 'Main AP auto-added'} onBlur={(v) => saveField(p, { cc_emails: v.split(/[,;\s]+/).filter(Boolean) })} />
                <LabeledInput label="Billing address" defaultValue={p.billing_address || ''} onBlur={(v) => saveField(p, { billing_address: v })} />
                <LabeledInput label="QuickBooks ref (future)" defaultValue={p.quickbooks_ref || ''} placeholder={p.kind === 'main_ap' ? company.name : `${company.name}:${p.name}`} onBlur={(v) => saveField(p, { quickbooks_ref: v })} />
              </div>
            </div>
          ))}

          {adding ? (
            <div className="rounded-md border border-cyan-700/40 bg-cyan-900/10 p-3 space-y-2" data-testid="pm-billing-add-form">
              <LabeledInput label="Subaccount name" testid="pm-billing-new-name" defaultValue="" placeholder="e.g. CHN / Brad Whitt" onBlur={(v) => setDraft((d) => ({ ...d, name: v }))} />
              <LabeledInput label="Invoice email(s)" testid="pm-billing-new-emails" defaultValue="" placeholder="brad.whitt@chninc.org" onBlur={(v) => setDraft((d) => ({ ...d, invoice_emails: v }))} />
              <div className="flex gap-2">
                <button onClick={createProfile} data-testid="pm-billing-new-save" className="bg-[#D4AF37] text-[#0A0A0A] text-sm font-semibold px-3 py-1.5 rounded inline-flex items-center gap-1"><Save className="w-4 h-4" /> Save subaccount</button>
                <button onClick={() => setAdding(false)} className="text-[#A3A3A3] text-sm px-3 py-1.5">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} data-testid="pm-billing-add-btn" className="w-full border border-dashed border-[#262626] rounded-md py-2.5 text-[#A3A3A3] hover:text-white hover:border-cyan-700/40 inline-flex items-center justify-center gap-1.5 text-sm"><Plus className="w-4 h-4" /> Add manager subaccount</button>
          )}
        </div>
      </div>
    </div>
  );
};

const LabeledInput = ({ label, defaultValue, placeholder, onBlur, testid }) => (
  <div className="space-y-1">
    <label className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</label>
    <input data-testid={testid} defaultValue={defaultValue} placeholder={placeholder} onBlur={(e) => onBlur(e.target.value)}
      className="w-full bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5 text-white text-sm" />
  </div>
);

export default PmBillingProfilesModal;
